"use client";

import { useRequestCallbackModalParams } from "@/hooks/request-callback-modal-params";
import { Button } from "@se/ui/components/button";
import { AnimatePresence, MotionConfig, motion } from "motion/react";
import { useTheme } from "next-themes";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { Logo } from "./logo";

export function useScrollY() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Set initial scroll position

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return scrollY;
}

export default function Navigation() {
  const { params, setParams } = useRequestCallbackModalParams();
  const scrollY = useScrollY();
  const { theme } = useTheme();
  const [active, setActive] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const dropdownRef = useRef<HTMLLIElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsServicesOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLinks = useMemo(
    () => [
      { id: 1, label: "Services", link: "#", hasDropdown: true },
      { id: 2, label: "JobWay", link: "/jobway" },
      { id: 3, label: "Pricing", link: "/pricing" },
      { id: 4, label: "Blog", link: "/blog" },
      { id: 5, label: "About", link: "/about" },
    ],
    [],
  );

  const serviceLinks = [
    { label: "SIP Accreditation", link: "/sip" },
    { label: "Solar Installation", link: "/solar" },
    { label: "Gas MAM Solutions", link: "/gas-mam" },
    { label: "Professional Services", link: "/professional-services" },
  ];

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrollY >= 120 ? "py-3" : "py-7 bg-background/80 backdrop-blur-sm"
      }`}
    >
      <nav className="relative mx-auto flex items-center justify-between max-w-7xl px-6">
        <motion.div
          animate={{
            y: scrollY >= 120 ? -50 : 0,
            opacity: scrollY >= 120 ? 0 : 1,
          }}
          transition={{ duration: 0.15 }}
        >
          <Link href="/">
            <Logo withLabel={true} />
          </Link>
        </motion.div>

        <ul className="relative z-10 hidden items-center justify-center gap-x-5 md:flex">
          <motion.div
            initial={{ x: 0 }}
            animate={{
              boxShadow:
                scrollY >= 120
                  ? theme === "dark"
                    ? "0 0 0 1px rgba(255,255,255,.08), 0 1px 2px -1px rgba(255,255,255,.08), 0 2px 4px rgba(255,255,255,.04)"
                    : "0 0 0 1px rgba(17,24,28,.08), 0 1px 2px -1px rgba(17,24,28,.08), 0 2px 4px rgba(17,24,28,.04)"
                  : "none",
              backgroundColor:
                scrollY >= 120
                  ? theme === "dark"
                    ? "rgba(17,17,17,0.95)"
                    : "rgba(255,255,255,0.95)"
                  : "transparent",
              backdropFilter: scrollY >= 120 ? "blur(8px)" : "none",
              border:
                scrollY >= 120
                  ? theme === "dark"
                    ? "1px solid rgba(255,255,255,0.1)"
                    : "1px solid rgba(0,0,0,0.1)"
                  : "1px solid transparent",
            }}
            transition={{
              ease: "linear",
              duration: 0.05,
              delay: 0.05,
            }}
            className={`flex h-auto min-h-[3rem] w-auto items-center justify-center rounded-full px-6 py-2.5 transition-all md:p-1.5 md:py-2 overflow-visible ${
              scrollY < 120 ? "bg-background" : ""
            }`}
          >
            <nav className="relative h-full items-center justify-between gap-x-3.5 md:flex overflow-visible">
              <ul className="relative flex h-full flex-col justify-center gap-6 md:flex-row md:justify-start md:gap-0 lg:gap-1 overflow-visible">
                {/* Mini Logo that appears when scrolled */}
                <motion.div
                  initial={{ width: 0 }}
                  animate={{
                    width: scrollY >= 120 ? "auto" : 0,
                  }}
                  transition={{
                    ease: "linear",
                    duration: 0.25,
                    delay: 0.05,
                  }}
                  className="overflow-hidden mr-2"
                >
                  <AnimatePresence>
                    {scrollY >= 120 && (
                      <motion.li
                        initial={{ x: "-125%" }}
                        animate={{ x: "0" }}
                        exit={{
                          x: "-125%",
                          transition: { ease: "linear", duration: 1 },
                        }}
                        transition={{ ease: "linear", duration: 0.3 }}
                        className="flex items-center justify-center shrink-0 whitespace-nowrap pl-3"
                      >
                        <Link href="/" className="flex items-center">
                          <Logo
                            withLabel={false}
                            className={`h-8 w-8 ${
                              theme === "dark"
                                ? "text-gray-200"
                                : "text-gray-700"
                            }`}
                          />
                        </Link>
                      </motion.li>
                    )}
                  </AnimatePresence>
                </motion.div>

                {navLinks.map((navItem) => (
                  <li
                    key={navItem.id}
                    className="relative flex items-center justify-center px-[0.75rem] py-[0.375rem] min-w-fit"
                    onMouseEnter={() =>
                      navItem.hasDropdown && setIsServicesOpen(true)
                    }
                    onMouseLeave={() =>
                      navItem.hasDropdown && setIsServicesOpen(false)
                    }
                    ref={navItem.hasDropdown ? dropdownRef : null}
                  >
                    {navItem.link.startsWith("mailto:") ? (
                      <a
                        href={navItem.link}
                        className="flex items-center font-medium text-foreground hover:text-primary transition-colors"
                      >
                        {navItem.label}
                        {navItem.hasDropdown && (
                          <svg
                            className="ml-1 w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        )}
                      </a>
                    ) : navItem.hasDropdown ? (
                      <button
                        type="button"
                        className={`flex items-center font-medium transition-colors cursor-pointer ${
                          scrollY >= 120
                            ? theme === "dark"
                              ? "text-gray-200 hover:text-white"
                              : "text-gray-700 hover:text-gray-900"
                            : "text-foreground hover:text-primary"
                        }`}
                        onClick={() => setIsServicesOpen(!isServicesOpen)}
                      >
                        {navItem.label}
                        <svg
                          className={`ml-1 w-4 h-4 transition-transform ${isServicesOpen ? "rotate-180" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                    ) : (
                      <Link
                        href={navItem.link}
                        className={`flex items-center font-medium transition-colors ${
                          scrollY >= 120
                            ? theme === "dark"
                              ? "text-gray-200 hover:text-white"
                              : "text-gray-700 hover:text-gray-900"
                            : "text-foreground hover:text-primary"
                        }`}
                      >
                        {navItem.label}
                      </Link>
                    )}

                    <AnimatePresence>
                      {navItem.hasDropdown && isServicesOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.15 }}
                          className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-gray-900 rounded-md shadow-xl border border-gray-200 dark:border-gray-700 z-30"
                        >
                          <div className="py-2">
                            {serviceLinks.map((service) => (
                              <Link
                                key={service.link}
                                href={service.link}
                                className="block px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                onClick={() => setIsServicesOpen(false)}
                              >
                                {service.label}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </li>
                ))}
              </ul>
            </nav>
            <motion.div
              initial={{ width: 0 }}
              animate={{
                width: scrollY >= 120 ? "auto" : 0,
              }}
              transition={{
                ease: "linear",
                duration: 0.25,
                delay: 0.05,
              }}
              className="!hidden rounded-full md:!block overflow-visible"
            >
              <AnimatePresence>
                {scrollY >= 120 && (
                  <motion.ul
                    initial={{ x: "125%" }}
                    animate={{ x: "0" }}
                    exit={{
                      x: "125%",
                      transition: { ease: "linear", duration: 1 },
                    }}
                    transition={{ ease: "linear", duration: 0.3 }}
                    className="shrink-0 whitespace-nowrap overflow-visible"
                  >
                    <li>
                      <Button
                        onClick={() => {
                          setActive(false);
                          setParams({
                            isOpen: true,
                            startingPoint: "navigation",
                          });
                        }}
                        className={`relative inline-flex w-fit items-center justify-center gap-x-1.5 overflow-hidden rounded-full px-3 py-1.5 font-medium outline-none transition-all ${
                          scrollY >= 120
                            ? theme === "dark"
                              ? "bg-white text-gray-900 hover:bg-gray-100"
                              : "bg-gray-900 text-white hover:bg-gray-800"
                            : "bg-primary text-primary-foreground hover:opacity-90"
                        }`}
                      >
                        Request Callback
                      </Button>
                    </li>
                  </motion.ul>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </ul>

        <motion.div
          className="z-10 hidden items-center gap-x-5 md:flex"
          animate={{
            y: scrollY >= 120 ? -50 : 0,
            opacity: scrollY >= 120 ? 0 : 1,
          }}
          transition={{ duration: 0.15 }}
        >
          <Button
            onClick={() => {
              setActive(false);
              setParams({ isOpen: true, startingPoint: "navigation" });
            }}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-full font-medium hover:opacity-90 transition-opacity"
          >
            Request Callback
          </Button>
        </motion.div>
        <MotionConfig transition={{ duration: 0.3, ease: "easeInOut" }}>
          <motion.button
            type="button"
            onClick={() => setActive((prev) => !prev)}
            animate={active ? "open" : "close"}
            className={`relative flex h-8 w-8 items-center justify-center rounded-md md:hidden ${
              scrollY >= 120 ? "bg-background/90" : ""
            }`}
          >
            <motion.span
              style={{ left: "50%", top: "35%", x: "-50%", y: "-50%" }}
              className={`absolute h-0.5 w-5 ${
                scrollY >= 120
                  ? theme === "dark"
                    ? "bg-gray-200"
                    : "bg-gray-700"
                  : "bg-black dark:bg-white"
              }`}
              variants={{
                open: {
                  rotate: ["0deg", "0deg", "45deg"],
                  top: ["35%", "50%", "50%"],
                },
                close: {
                  rotate: ["45deg", "0deg", "0deg"],
                  top: ["50%", "50%", "35%"],
                },
              }}
              transition={{ duration: 0.3 }}
            />
            <motion.span
              style={{ left: "50%", top: "50%", x: "-50%", y: "-50%" }}
              className={`absolute h-0.5 w-5 ${
                scrollY >= 120
                  ? theme === "dark"
                    ? "bg-gray-200"
                    : "bg-gray-700"
                  : "bg-black dark:bg-white"
              }`}
              variants={{
                open: {
                  opacity: 0,
                },
                close: {
                  opacity: 1,
                },
              }}
            />
            <motion.span
              style={{ left: "50%", bottom: "30%", x: "-50%", y: "-50%" }}
              className={`absolute h-0.5 w-5 ${
                scrollY >= 120
                  ? theme === "dark"
                    ? "bg-gray-200"
                    : "bg-gray-700"
                  : "bg-black dark:bg-white"
              }`}
              variants={{
                open: {
                  rotate: ["0deg", "0deg", "-45deg"],
                  top: ["65%", "50%", "50%"],
                },
                close: {
                  rotate: ["-45deg", "0deg", "0deg"],
                  top: ["50%", "50%", "65%"],
                },
              }}
              transition={{ duration: 0.3 }}
            />
          </motion.button>
        </MotionConfig>

        {/* Mobile Menu */}
        <AnimatePresence>
          {active && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className={`absolute left-0 right-0 mt-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 md:hidden ${
                scrollY >= 120 ? "top-12" : "top-full"
              }`}
            >
              <ul className="space-y-4">
                {navLinks.map((navItem) => (
                  <li key={navItem.id}>
                    {navItem.link.startsWith("mailto:") ? (
                      <a
                        href={navItem.link}
                        className="block py-2 font-medium text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition"
                        onClick={() => setActive(false)}
                      >
                        {navItem.label}
                      </a>
                    ) : (
                      <div>
                        {navItem.hasDropdown ? (
                          <span className="block py-2 font-medium text-gray-700 dark:text-gray-200 cursor-pointer">
                            {navItem.label}
                          </span>
                        ) : (
                          <Link
                            href={navItem.link}
                            className="block py-2 font-medium text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition"
                            onClick={() => setActive(false)}
                          >
                            {navItem.label}
                          </Link>
                        )}
                        {navItem.hasDropdown && (
                          <div className="ml-4 mt-2 space-y-2">
                            {serviceLinks.map((service) => (
                              <Link
                                key={service.link}
                                href={service.link}
                                className="block py-1 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition"
                                onClick={() => setActive(false)}
                              >
                                {service.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                ))}
                <li className="pt-4">
                  <Button
                    className="block bg-primary text-primary-foreground px-4 py-2 rounded-full text-center font-medium hover:opacity-90 transition-opacity"
                    onClick={() => {
                      setActive(false);
                      setParams({ isOpen: true, startingPoint: "navigation" });
                    }}
                  >
                    Request Callback
                  </Button>
                </li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}
